<script setup lang="ts">
// En-tête de page publique : texte à gauche, photo à droite (empilés sous le seuil lg).
// Le bandeau de fond est fourni par le parent (header.public-hero.public-band ou section).
withDefaults(defineProps<{
  image?: string
  imageAlt?: string
  imageWidth?: number
  imageHeight?: number
  dark?: boolean
  warm?: boolean
}>(), {
  image: undefined,
  imageAlt: '',
  imageWidth: 720,
  imageHeight: 540,
  dark: false,
  warm: false,
})
</script>

<template>
  <div
    class="mx-auto grid max-w-7xl gap-8 px-6 py-8 md:px-12 md:py-14 lg:items-center xl:px-6"
    :class="image || $slots.aside ? 'lg:grid-cols-12' : ''"
  >
    <div :class="image || $slots.aside ? 'lg:col-span-8' : 'max-w-4xl'">
      <div
        v-if="$slots.eyebrow"
        class="public-rise mb-5 flex flex-wrap gap-2"
        style="--rise-index: 0"
      >
        <p
          class="public-kicker"
          :class="[dark && '!border-brand-blue-400/40 !bg-white/10 !text-brand-blue-100', warm && '!border-brand-red-200 !bg-brand-red-50 !text-brand-red-700 dark:!border-brand-red-900/60 dark:!bg-brand-red-950/50 dark:!text-brand-red-200']"
        >
          <slot name="eyebrow" />
        </p>
        <p
          v-if="$slots.badge"
          class="public-kicker"
          :class="dark && '!border-brand-blue-400/40 !bg-white/10 !text-brand-blue-100'"
        >
          <slot name="badge" />
        </p>
      </div>
      <h1
        class="public-h1 public-rise"
        :class="dark && 'text-white'"
        style="--rise-index: 1"
      >
        <slot name="title" />
      </h1>
      <p
        v-if="$slots.lead"
        class="public-lead public-rise mt-5"
        :class="dark && '!text-brand-blue-100'"
        style="--rise-index: 2"
      >
        <slot name="lead" />
      </p>
      <div
        v-if="$slots.actions"
        class="public-rise mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
        style="--rise-index: 3"
        data-track-zone="hero"
      >
        <slot name="actions" />
      </div>
      <slot />
    </div>

    <figure
      v-if="image"
      class="public-rise lg:col-span-4"
      style="--rise-index: 2"
    >
      <NuxtImg
        :src="image"
        :alt="imageAlt"
        :width="imageWidth"
        :height="imageHeight"
        sizes="640px lg:400px"
        format="webp"
        class="aspect-[16/10] w-full rounded-[var(--radius-section)] border border-[var(--line-soft)] object-cover lg:aspect-[4/3]"
        loading="eager"
        fetchpriority="high"
        preload
      />
      <figcaption
        v-if="$slots.caption"
        class="mt-3 text-sm leading-relaxed"
        :class="dark ? 'text-brand-blue-100' : 'public-muted'"
      >
        <slot name="caption" />
      </figcaption>
    </figure>
    <div
      v-else-if="$slots.aside"
      class="lg:col-span-4"
    >
      <slot name="aside" />
    </div>
  </div>
</template>
